import React, { useEffect, useState, useCallback } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader, Card, Button, Modal, Field, Input, Select, Empty } from "@/components/ui-kit";
import { FileText, Sheet, ShoppingCart, Printer, CheckCircle2, Circle, Tag, Boxes, PackageCheck, UserPlus, AlertTriangle, History, SlidersHorizontal, ClipboardList, PackagePlus, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  a_commander: { label: "À commander", Icon: Circle, cls: "text-red-500", badge: "bg-red-500/15 text-red-500 ring-1 ring-red-500/30" },
  en_commande: { label: "En commande", Icon: ShoppingCart, cls: "text-orange-500", badge: "bg-orange-500/15 text-orange-500 ring-1 ring-orange-500/30" },
  au_flocage: { label: "Au flocage", Icon: Printer, cls: "text-blue-400", badge: "bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30" },
  distribue: { label: "Distribué", Icon: CheckCircle2, cls: "text-primary", badge: "bg-primary/15 text-primary ring-1 ring-primary/30" },
};

const MTYPE_META = {
  affilie: { label: "Affilié", cls: "bg-primary/15 text-primary" },
  essai: { label: "Essai", cls: "bg-yellow-500/15 text-yellow-500" },
  seance: { label: "Séance", cls: "bg-blue-500/15 text-blue-400" },
  staff: { label: "Staff", cls: "bg-purple-500/15 text-purple-400" },
};
const PAY_META = {
  paye: { label: "Payé", cls: "bg-primary/15 text-primary" },
  partiel: { label: "Partiel", cls: "bg-orange-500/15 text-orange-500" },
  non_paye: { label: "Non payé", cls: "bg-red-500/15 text-red-500" },
  na: { label: "—", cls: "bg-secondary text-muted-foreground" },
};
const ORDER_STATES = [
  { key: "non_commande", label: "Non commandé" },
  { key: "a_commander", label: "À commander" },
  { key: "commande", label: "Commandé" },
  { key: "au_flocage", label: "Au flocage" },
  { key: "distribue", label: "Distribué" },
];
const ORDER_CLS = {
  non_commande: "bg-secondary text-muted-foreground",
  a_commander: "bg-red-500/15 text-red-500",
  commande: "bg-primary/15 text-primary",
  au_flocage: "bg-blue-500/15 text-blue-500",
  distribue: "bg-green-500/15 text-green-500",
};
const MTYPES = [
  { key: "all", label: "Tous types" },
  { key: "affilie", label: "Affilié" },
  { key: "essai", label: "Essai" },
  { key: "seance", label: "À la séance" },
];
const orderInfo = (m) => {
  if (m.membership_type === "essai") return { label: "Essai — non commandé", cls: "bg-yellow-500/15 text-yellow-500" };
  if (m.force_order) return { label: "Commande forcée", cls: "bg-primary/15 text-primary" };
  if (m.orderable) return { label: "Payé — à commander", cls: "bg-primary/15 text-primary" };
  return { label: "Non payé — non commandé", cls: "bg-destructive/15 text-destructive" };
};

const currentSeason = () => {
  const d = new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 7 ? y : y - 1;
  return `${start}-${start + 1}`;
};
const seasonOptions = () => {
  const cur = parseInt(currentSeason().split("-")[0]);
  const arr = [];
  for (let s = cur + 1; s >= cur - 3; s--) arr.push(`${s}-${s + 1}`);
  return arr;
};

export default function Equipment() {
  const [articles, setArticles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [colors, setColors] = useState([]);
  const [stockStatuses, setStockStatuses] = useState([]);
  const [stockOpen, setStockOpen] = useState(false);
  const [stock, setStock] = useState({});
  const [stockSummary, setStockSummary] = useState({});
  const [lowThreshold, setLowThreshold] = useState(5);
  const [thresholds, setThresholds] = useState({});
  const [lowArticles, setLowArticles] = useState([]);
  const [threshOpen, setThreshOpen] = useState(false);
  const [threshData, setThreshData] = useState({});
  const [savingThresh, setSavingThresh] = useState(false);
  const [movesOpen, setMovesOpen] = useState(false);
  const [moves, setMoves] = useState([]);
  const [moveArt, setMoveArt] = useState("");
  const [restockOpen, setRestockOpen] = useState(false);
  const [restock, setRestock] = useState({ items: [], total_units: 0, lines: 0 });
  const [prefilling, setPrefilling] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [denied, setDenied] = useState(false);
  const [season, setSeason] = useState(currentSeason());
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [mFilter, setMFilter] = useState("all");
  const [payFilter, setPayFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("all");
  const [sel, setSel] = useState(new Set());
  const [refsOpen, setRefsOpen] = useState(false);
  const [refsData, setRefsData] = useState({});
  const [refsIsAdmin, setRefsIsAdmin] = useState(false);
  const [refsPackType, setRefsPackType] = useState("joueur");
  const [savingRefs, setSavingRefs] = useState(false);
  const [ordering, setOrdering] = useState(false);

  const packLabel = (k) => (packTypes.find((t) => t.key === k) || {}).label || k;

  const load = useCallback(() => {
    api.get(`/equipment?season=${season}`).then((r) => setList(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, [season]);
  const loadRefs = useCallback(() => {
    api.get(`/equipment/pack-refs?season=${season}`).then((r) => { setRefsData(r.data.refs || {}); setRefsIsAdmin(!!r.data.is_admin); }).catch(() => {});
  }, [season]);
  useEffect(() => {
    api.get("/equipment/config").then((r) => { setArticles(r.data.articles); setStatuses(r.data.statuses); setPackTypes(r.data.pack_types || []); setColors(r.data.colors || []); setStockStatuses(r.data.stock_statuses || []); }).catch(() => {});
    loadStock();
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const openEdit = async (row) => {
    const { data } = await api.get(`/equipment/${row.member_id}`);
    setEditing(data);
    setForm({ pack_type: data.pack_type || "joueur", force_order: !!data.force_order, flocage_number: data.flocage_number || "", flocage_name: data.flocage_name || "", items: { ...data.items } });
  };
  const setItem = (key, field, value) => setForm((f) => ({ ...f, items: { ...f.items, [key]: { ...f.items[key], [field]: value } } }));
  const loadStock = () => api.get("/equipment/stock").then((r) => { setStock(r.data.stock || {}); setStockSummary(r.data.summary || {}); setLowThreshold(r.data.low_threshold ?? 5); setThresholds(r.data.thresholds || {}); setLowArticles(r.data.low_articles || []); }).catch(() => {});
  const thOf = (article, color) => {
    const a = thresholds[article] || {};
    if (a[color] != null) return a[color];
    if (a._default != null) return a._default;
    if (typeof thresholds._default === "number") return thresholds._default;
    return lowThreshold;
  };
  const openStock = () => { loadStock(); setStockOpen(true); };
  const openThresholds = () => { setThreshData(JSON.parse(JSON.stringify(thresholds || {}))); setThreshOpen(true); };
  const setThresh = (article, color, value) => setThreshData((d) => ({ ...d, [article]: { ...(d[article] || {}), [color]: value === "" ? "" : Math.max(0, parseInt(value) || 0) } }));
  const saveThresholds = async () => {
    setSavingThresh(true);
    try { const { data } = await api.put("/equipment/thresholds", { thresholds: threshData }); setThresholds(data.thresholds || {}); toast.success("Seuils enregistrés"); setThreshOpen(false); loadStock(); }
    catch { toast.error("Erreur"); } finally { setSavingThresh(false); }
  };
  const openMoves = (art = "") => { setMoveArt(art); api.get(`/equipment/movements${art ? `?article=${art}` : ""}`).then((r) => setMoves(r.data.movements || [])).catch(() => {}); setMovesOpen(true); };
  const filterMoves = (art) => { setMoveArt(art); api.get(`/equipment/movements${art ? `?article=${art}` : ""}`).then((r) => setMoves(r.data.movements || [])).catch(() => {}); };
  const prefillStock = async () => {
    setPrefilling(true);
    try { const { data } = await api.post(`/equipment/stock/prefill?season=${season}`); toast.success(`Stock pré-rempli : ${data.lines} ligne(s) depuis les commandes en cours`); loadStock(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setPrefilling(false); }
  };
  const openRestock = () => { api.get(`/equipment/restock?season=${season}`).then((r) => setRestock(r.data)).catch(() => {}); setRestockOpen(true); };
  const openAdd = () => { api.get("/equipment/eligible-users").then((r) => setEligible(r.data)).catch(() => {}); setAddOpen(true); };
  const addPerson = async (uid) => {
    try { await api.post("/equipment/add-person", { user_id: uid }); toast.success("Membre ajouté au pack"); setAddOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const saveStockCell = async (article, color, size, patch) => {
    const cur = ((stock[article] || {})[color] || {})[size] || { qty: 0, status: "en_stock" };
    const next = { ...cur, ...patch };
    setStock((s) => ({ ...s, [article]: { ...(s[article] || {}), [color]: { ...((s[article] || {})[color] || {}), [size]: next } } }));
    try { await api.put("/equipment/stock", { article, color, size, qty: next.qty, status: next.status }); }
    catch { toast.error("Erreur stock"); }
  };
  const addStockSize = async (article, color, size) => {
    if (!size) return;
    await saveStockCell(article, color, size, { qty: 0, status: "a_commander" });
    toast.success("Taille ajoutée");
  };
  const markReceived = async (m, received) => {
    try { const { data } = await api.post(`/equipment/${m.member_id}/receive`, { received }); toast.success(received ? "Pack marqué reçu (stock déduit)" : "Réception annulée"); setList((l) => l.map((x) => x.member_id === m.member_id ? data : x)); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const save = async () => {
    await api.put(`/equipment/${editing.member_id}`, form);
    toast.success("Pack enregistré");
    setEditing(null); load();
  };

  const setRef = (art, field, value) => setRefsData((d) => ({
    ...d, [refsPackType]: { ...(d[refsPackType] || {}), [art]: { ...((d[refsPackType] || {})[art] || {}), [field]: value } },
  }));
  const saveRefs = async () => {
    setSavingRefs(true);
    try {
      await api.put("/equipment/pack-refs", { season, pack_type: refsPackType, refs: refsData[refsPackType] || {} });
      toast.success(`Références ${packLabel(refsPackType)} enregistrées`);
    } catch { toast.error("Erreur"); } finally { setSavingRefs(false); }
  };
  const orderToTreasury = async () => {
    setOrdering(true);
    try {
      const { data } = await api.post("/equipment/order-to-treasury", { season, pack_type: refsPackType });
      toast.success(`Commande mise en attente en trésorerie : ${data.amount} € (${data.items} article(s))`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setOrdering(false); }
  };
  const money = (v) => (Number(v) || 0).toFixed(2);

  const counts = { a_commander: 0, en_commande: 0, au_flocage: 0, distribue: 0 };
  list.forEach((m) => Object.values(m.items || {}).forEach((it) => { if (it.size) counts[it.status] = (counts[it.status] || 0) + 1; }));
  const shown = list.filter((m) => (filter === "all" || m.pack_type === filter) && (mFilter === "all" || m.membership_type === mFilter) && (payFilter === "all" || m.payment_status === payFilter) && (orderFilter === "all" || m.order_state === orderFilter) && (!search.trim() || `${m.name} ${m.category || ""} ${m.flocage_name || ""} ${m.flocage_number || ""}`.toLowerCase().includes(search.trim().toLowerCase())));

  const setOrderState = async (m, state) => {
    const prev = m.order_state;
    setList((l) => l.map((x) => x.member_id === m.member_id ? { ...x, order_state: state, order_state_manual: true } : x));
    try { await api.patch(`/equipment/${m.member_id}/order-state`, { order_state: state }); }
    catch { toast.error("Erreur"); setList((l) => l.map((x) => x.member_id === m.member_id ? { ...x, order_state: prev } : x)); }
  };
  const membersExportQuery = () => `season=${season}&order_state=${orderFilter}&pay=${payFilter}&mtype=${mFilter}&pack=${filter}`;
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = () => setSel((p) => (p.size === shown.length && shown.length ? new Set() : new Set(shown.map((m) => m.member_id))));
  const downloadSelected = () => { if (!sel.size) return; downloadFile(`/equipment/packs.pdf?ids=${Array.from(sel).join(",")}`, "fiches_vetements.pdf").catch(() => toast.error("Export échoué")); };
  const downloadFlocage = () => { if (!sel.size) return; downloadFile(`/equipment/flocage.pdf?ids=${Array.from(sel).join(",")}`, "flocage_selection.pdf").catch(() => toast.error("Export échoué")); };

  if (denied) return (
    <div><PageHeader title="Pack vêtements" subtitle="Accès réservé." />
      <Empty text="Vous n'avez pas les droits pour gérer le pack vêtements. Demandez à l'administrateur de vous désigner comme responsable pack." /></div>
  );

  return (
    <div>
      <PageHeader title="Pack vêtements" subtitle="Tailles, flocage, références fournisseur et suivi des commandes."
        action={<div className="flex gap-2 flex-wrap">
          <Button data-testid="pack-refs-btn" variant="outline" onClick={() => setRefsOpen(true)}><Tag size={16} /> Références packs</Button>
          {refsIsAdmin && <Button data-testid="stock-btn" variant="outline" onClick={openStock}><Boxes size={16} /> Stock</Button>}
          {refsIsAdmin && <Button data-testid="add-person-btn" variant="outline" onClick={openAdd}><UserPlus size={16} /> Ajouter un membre</Button>}
          <Button data-testid="download-selected-packs" variant="outline" onClick={downloadSelected} disabled={!sel.size}><FileText size={16} /> Fiches sélection ({sel.size})</Button>
          <Button data-testid="download-flocage-selection" variant="outline" onClick={downloadFlocage} disabled={!sel.size}><Printer size={16} /> Flocage sélection ({sel.size})</Button>
          <Button data-testid="members-pdf-btn" variant="outline" onClick={() => downloadFile(`/equipment/members.pdf?${membersExportQuery()}`, "vetements_membres.pdf").catch(() => toast.error("Export échoué"))}><FileText size={16} /> Imprimer la liste</Button>
          <Button data-testid="order-pdf-btn" variant="outline" onClick={() => downloadFile(`/equipment/order.pdf?season=${season}`, "commande_vetements.pdf").catch(() => toast.error("Export échoué"))}><FileText size={16} /> Commande PDF</Button>
          <Button data-testid="order-xlsx-btn" variant="outline" onClick={() => downloadFile(`/equipment/order.xlsx?season=${season}`, "commande_vetements.xlsx").catch(() => toast.error("Export échoué"))}><Sheet size={16} /> Commande Excel</Button>
        </div>} />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="pack-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un joueur..."
            className="pl-9 pr-3 h-10 w-56 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Saison</span>
          <Select data-testid="pack-season-select" value={season} onChange={(e) => setSeason(e.target.value)} className="w-40">
            {seasonOptions().map((s) => <option key={s} value={s}>Saison {s}</option>)}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Adhésion</span>
          <Select data-testid="pack-mtype-filter" value={mFilter} onChange={(e) => setMFilter(e.target.value)} className="w-36">
            {MTYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Paiement</span>
          <Select data-testid="pack-pay-filter" value={payFilter} onChange={(e) => setPayFilter(e.target.value)} className="w-36">
            <option value="all">Tous</option>
            <option value="paye">Payé</option>
            <option value="partiel">Partiel</option>
            <option value="non_paye">Non payé</option>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Commande</span>
          <Select data-testid="pack-order-filter" value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)} className="w-40">
            <option value="all">Toutes</option>
            {ORDER_STATES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </Select>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button data-testid="pack-filter-all" onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filter === "all" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>Tous</button>
          {packTypes.map((t) => (
            <button key={t.key} data-testid={`pack-filter-${t.key}`} onClick={() => setFilter(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filter === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{t.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {statuses.map((s) => { const M = STATUS_META[s.key] || STATUS_META.a_commander; return (
          <Card key={s.key} data-testid={`summary-${s.key}`} className="flex items-center gap-3 py-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/15"><M.Icon size={18} className={M.cls} /></div>
            <div><p className="text-2xl font-black leading-none">{counts[s.key] || 0}</p><p className="text-xs text-muted-foreground mt-1">{s.label}</p></div>
          </Card>); })}
      </div>

      {refsIsAdmin && Object.keys(stockSummary).length > 0 && (
        <Card className="mb-6 py-4" data-testid="stock-synthese">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Boxes size={14} /> Synthèse du stock (total par article)</p>
            <div className="flex gap-2">
              <button data-testid="inventory-pdf" onClick={() => downloadFile("/equipment/inventory.pdf", "inventaire_stock.pdf").catch(() => toast.error("Export échoué"))} className="text-xs font-bold text-muted-foreground hover:text-foreground flex items-center gap-1"><FileText size={13} /> PDF</button>
              <button data-testid="inventory-xlsx" onClick={() => downloadFile("/equipment/inventory.xlsx", "inventaire_stock.xlsx").catch(() => toast.error("Export échoué"))} className="text-xs font-bold text-muted-foreground hover:text-foreground flex items-center gap-1"><Sheet size={13} /> Excel</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {articles.map((a) => { const tot = stockSummary[a.key] || 0; const low = lowArticles.includes(a.key); return (
              <span key={a.key} data-testid={`stock-total-${a.key}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${low ? "bg-destructive/15 text-destructive" : "bg-secondary text-foreground"}`}>
                {low && <AlertTriangle size={12} />} {a.label}: {tot}
              </span>); })}
          </div>
        </Card>
      )}

      {shown.length ? (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm hidden md:table" data-testid="equipment-table">
            <thead><tr className="border-b border-border text-left">
              <th className="px-3 py-3 w-8"><input type="checkbox" data-testid="pack-select-all" className="w-4 h-4 accent-[color:var(--primary)]" checked={sel.size === shown.length && shown.length > 0} onChange={toggleSelAll} /></th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">Membre</th>
              <th className="px-3 py-3 text-xs uppercase text-muted-foreground whitespace-nowrap">Pack</th>
              <th className="px-3 py-3 text-xs uppercase text-muted-foreground whitespace-nowrap">Flocage</th>
              {articles.map((a) => <th key={a.key} className="px-3 py-3 text-xs uppercase text-muted-foreground text-center whitespace-nowrap">{a.label}</th>)}
              <th className="px-3 py-3"></th>
            </tr></thead>
            <tbody>
              {shown.map((m) => (
                <tr key={m.member_id} data-testid="equipment-row" className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="px-3 py-3"><input type="checkbox" data-testid={`pack-select-${m.member_id}`} className="w-4 h-4 accent-[color:var(--primary)]" checked={sel.has(m.member_id)} onChange={() => toggleSel(m.member_id)} /></td>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">{m.name}
                    <span className="block text-xs text-muted-foreground font-normal">{m.category}</span>
                    <span data-testid={`mtype-badge-${m.member_id}`} className={`inline-block mt-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${(MTYPE_META[m.membership_type] || MTYPE_META.affilie).cls}`}>{(MTYPE_META[m.membership_type] || MTYPE_META.affilie).label}</span>
                    {m.payment_status && m.payment_status !== "na" && (
                      <span data-testid={`pay-badge-${m.member_id}`} title={`Payé ${m.payment_paid} € / ${m.payment_total} €`}
                        className={`inline-block mt-1 ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${(PAY_META[m.payment_status] || PAY_META.na).cls}`}>{(PAY_META[m.payment_status] || PAY_META.na).label}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">
                    <span className="rounded-full bg-secondary px-2 py-0.5 font-bold">{packLabel(m.pack_type)}</span>
                    {(() => { const oi = orderInfo(m); return <span data-testid={`order-status-${m.member_id}`} className={`block mt-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-center ${oi.cls}`}>{oi.label}</span>; })()}
                    {refsIsAdmin ? (
                      <select data-testid={`order-state-${m.member_id}`} value={m.order_state || "non_commande"} onChange={(e) => setOrderState(m, e.target.value)}
                        className={`mt-1 w-full rounded-full px-2 py-1 text-[10px] font-bold text-center border-0 outline-none cursor-pointer ${ORDER_CLS[m.order_state] || ORDER_CLS.non_commande}`}>
                        {ORDER_STATES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    ) : (
                      <span className={`block mt-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-center ${ORDER_CLS[m.order_state] || ORDER_CLS.non_commande}`}>{(ORDER_STATES.find((o) => o.key === m.order_state) || {}).label || "Non commandé"}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">{m.flocage_number ? `#${m.flocage_number}` : "—"} {m.flocage_name}</td>
                  {articles.map((a) => { const it = m.items[a.key] || {}; const M = STATUS_META[it.status] || STATUS_META.a_commander; return (
                    <td key={a.key} className="px-3 py-3 text-center">
                      {it.size
                        ? <span data-testid={`pack-size-status-${m.member_id}-${a.key}`} title={M.label}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${M.badge}`}><M.Icon size={12} /> {it.size}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>); })}
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button data-testid={`receive-pack-${m.member_id}`} onClick={() => markReceived(m, !m.received)} title={m.received ? "Annuler la réception" : "Marquer le pack reçu (déduit le stock)"} className={`text-xs font-bold mr-3 ${m.received ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>{m.received ? "✓ Reçu" : "Pack reçu"}</button>
                    <button data-testid={`edit-pack-${m.member_id}`} onClick={() => openEdit(m)} className="text-xs font-bold text-primary hover:underline mr-3">Encoder</button>
                    <button data-testid={`pack-pdf-${m.member_id}`} onClick={() => downloadFile(`/equipment/member/${m.member_id}.pdf`, `pack_${m.name}.pdf`).catch(() => toast.error("Export échoué"))} className="text-xs font-bold text-muted-foreground hover:text-foreground">Fiche PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border/60">
            {shown.map((m) => (
              <div key={m.member_id} data-testid="equipment-card" className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-2">
                    <input type="checkbox" data-testid={`pack-select-m-${m.member_id}`} className="w-4 h-4 mt-1 accent-[color:var(--primary)]" checked={sel.has(m.member_id)} onChange={() => toggleSel(m.member_id)} />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{m.name}</p>
                      <p className="text-xs text-muted-foreground">{m.category}{(m.flocage_number || m.flocage_name) ? ` · ${m.flocage_number ? `#${m.flocage_number}` : ""} ${m.flocage_name || ""}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-bold">{packLabel(m.pack_type)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${(MTYPE_META[m.membership_type] || MTYPE_META.affilie).cls}`}>{(MTYPE_META[m.membership_type] || MTYPE_META.affilie).label}</span>
                    {m.payment_status && m.payment_status !== "na" && (
                      <span data-testid={`pay-badge-m-${m.member_id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${(PAY_META[m.payment_status] || PAY_META.na).cls}`}>{(PAY_META[m.payment_status] || PAY_META.na).label}</span>
                    )}
                    {refsIsAdmin ? (
                      <select data-testid={`order-state-m-${m.member_id}`} value={m.order_state || "non_commande"} onChange={(e) => setOrderState(m, e.target.value)}
                        className={`rounded-full px-2 py-1 text-[10px] font-bold border-0 outline-none cursor-pointer ${ORDER_CLS[m.order_state] || ORDER_CLS.non_commande}`}>
                        {ORDER_STATES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    ) : (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ORDER_CLS[m.order_state] || ORDER_CLS.non_commande}`}>{(ORDER_STATES.find((o) => o.key === m.order_state) || {}).label || "Non commandé"}</span>
                    )}
                  </div>
                </div>
                {(() => { const oi = orderInfo(m); return <span className={`inline-block mt-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${oi.cls}`}>{oi.label}</span>; })()}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3">
                  {articles.map((a) => { const it = m.items[a.key] || {}; if (!it.size) return null; const M = STATUS_META[it.status] || STATUS_META.a_commander; return (
                    <div key={a.key} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-muted-foreground truncate">{a.label}</span>
                      <span data-testid={`pack-size-status-m-${m.member_id}-${a.key}`} title={M.label} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-bold shrink-0 ${M.badge}`}>{it.size} <M.Icon size={11} /></span>
                    </div>); })}
                  {articles.every((a) => !(m.items[a.key] || {}).size) && <p className="text-xs text-muted-foreground col-span-2">Aucun article encodé.</p>}
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`receive-pack-m-${m.member_id}`} onClick={() => markReceived(m, !m.received)} className={`flex-1 h-10 rounded-lg text-xs font-bold ${m.received ? "bg-primary/20 text-primary" : "bg-secondary text-foreground"}`}><PackageCheck size={14} className="inline mr-1" />{m.received ? "Reçu" : "Pack reçu"}</button>
                  <button data-testid={`edit-pack-m-${m.member_id}`} onClick={() => openEdit(m)} className="flex-1 h-10 rounded-lg bg-primary text-primary-foreground text-xs font-bold">Encoder</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : <Empty text="Aucun membre pour ce filtre." />}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Pack — ${editing?.name || ""}`}>
        {form && (
          <div className="space-y-3">
            <Field label="Type de pack">
              <Select data-testid="pack-type-select" value={form.pack_type} onChange={(e) => setForm({ ...form, pack_type: e.target.value })}>
                {packTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Select>
            </Field>
            {editing?.membership_type === "essai" ? (
              <div data-testid="pack-essai-note" className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3 text-xs text-yellow-600 dark:text-yellow-400">
                Membre en <b>Essai</b> : son pack n'est jamais inclus dans la commande fournisseur ni en trésorerie.
              </div>
            ) : refsIsAdmin && (
              <label data-testid="pack-force-order" className="flex items-start gap-2 rounded-lg bg-secondary/40 p-3 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[color:var(--primary)]"
                  checked={!!form.force_order} onChange={(e) => setForm({ ...form, force_order: e.target.checked })} />
                <span className="text-xs">
                  <b>Forcer la commande</b> (modification administrateur)
                  <span className="block text-muted-foreground">Par défaut, un pack n'est commandé que si la cotisation Pack est payée (au moins un acompte). Cochez pour l'inclure malgré un paiement non réglé.</span>
                </span>
              </label>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Flocage numéro"><Input data-testid="pack-flocage-number" value={form.flocage_number} onChange={(e) => setForm({ ...form, flocage_number: e.target.value })} /></Field>
              <Field label="Identifiant (nom floqué)"><Input data-testid="pack-flocage-name" value={form.flocage_name} onChange={(e) => setForm({ ...form, flocage_name: e.target.value })} /></Field>
            </div>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
              {articles.map((a) => { const it = form.items[a.key] || { size: "", color: "", qty: 0, status: "a_commander" }; return (
                <div key={a.key} className="flex flex-col gap-2 p-2 rounded-lg bg-secondary/40">
                  <span className="font-semibold text-sm">{a.label}</span>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Select data-testid={`pack-size-${a.key}`} value={it.size} onChange={(e) => setItem(a.key, "size", e.target.value)}>
                      <option value="">Taille…</option>
                      {it.size && !a.sizes.includes(it.size) && <option value={it.size}>{it.size} (ancienne)</option>}
                      {a.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                    <Select data-testid={`pack-color-${a.key}`} value={it.color || ""} onChange={(e) => setItem(a.key, "color", e.target.value)}>
                      <option value="">Couleur…</option>
                      {colors.map((c) => <option key={c.key} value={c.key}>{c.label} ({c.code})</option>)}
                    </Select>
                    <Input data-testid={`pack-qty-${a.key}`} type="number" min="0" placeholder="Qté" value={it.qty || ""} onChange={(e) => setItem(a.key, "qty", e.target.value)} />
                    <Select data-testid={`pack-status-${a.key}`} value={it.status} onChange={(e) => setItem(a.key, "status", e.target.value)}>
                      {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </Select>
                  </div>
                </div>); })}
            </div>
            <Button data-testid="save-pack-btn" className="w-full" onClick={save}>Enregistrer</Button>
          </div>
        )}
      </Modal>

      <Modal open={refsOpen} onClose={() => setRefsOpen(false)} title={`Références des packs — Saison ${season}`}>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Pour chaque article du pack, indiquez la référence fournisseur et une référence interne (visibles sur les listes de commande){refsIsAdmin ? ", ainsi que les prix (visibles par l'administrateur uniquement)." : "."}</p>
          <div className="flex gap-1.5 flex-wrap">
            {packTypes.map((t) => (
              <button key={t.key} data-testid={`refs-tab-${t.key}`} onClick={() => setRefsPackType(t.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${refsPackType === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{t.label}</button>
            ))}
          </div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {articles.map((a) => { const r = (refsData[refsPackType] || {})[a.key] || {};
              const buy = Number(r.buy) || 0, floc = Number(r.floc) || 0;
              const net = buy * 0.6, revient = net + floc, vente = buy + floc;
              return (
              <div key={a.key} className="p-2 rounded-lg bg-secondary/40">
                <p className="font-semibold text-sm mb-2">{a.label}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input data-testid={`ref-supplier-${a.key}`} value={r.supplier || ""} placeholder="Réf. fournisseur" onChange={(e) => setRef(a.key, "supplier", e.target.value)} />
                  <Input data-testid={`ref-internal-${a.key}`} value={r.internal || ""} placeholder="Réf. interne" onChange={(e) => setRef(a.key, "internal", e.target.value)} />
                </div>
                {refsIsAdmin && (
                  <div className="mt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Prix achat (€)</label>
                        <Input data-testid={`ref-buy-${a.key}`} type="number" step="0.01" min="0" value={r.buy ?? ""} placeholder="0.00" onChange={(e) => setRef(a.key, "buy", e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">Flocage (€)</label>
                        <Input data-testid={`ref-floc-${a.key}`} type="number" step="0.01" min="0" value={r.floc ?? ""} placeholder="0.00" onChange={(e) => setRef(a.key, "floc", e.target.value)} />
                      </div>
                    </div>
                    <div data-testid={`ref-calc-${a.key}`} className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
                      <span className="rounded bg-secondary px-2 py-1">Achat net <b className="block text-foreground">{money(net)} €</b></span>
                      <span className="rounded bg-secondary px-2 py-1">Flocage <b className="block text-foreground">{money(floc)} €</b></span>
                      <span className="rounded bg-primary/15 px-2 py-1 text-primary">Prix de revient <b className="block">{money(revient)} €</b></span>
                      <span className="rounded bg-secondary px-2 py-1">Prix de vente <b className="block text-foreground">{money(vente)} €</b></span>
                    </div>
                  </div>
                )}
              </div>); })}
          </div>
          <Button data-testid="save-refs-btn" className="w-full" onClick={saveRefs} disabled={savingRefs}>{savingRefs ? "Enregistrement..." : `Enregistrer ${packLabel(refsPackType)}`}</Button>
          {refsIsAdmin && (
            <Button data-testid="order-to-treasury-btn" variant="outline" className="w-full" onClick={orderToTreasury} disabled={ordering}>
              <ShoppingCart size={16} /> {ordering ? "..." : `Mettre la commande ${packLabel(refsPackType)} en attente (Trésorerie)`}
            </Button>
          )}
        </div>
      </Modal>

      <Modal open={stockOpen} onClose={() => setStockOpen(false)} title="Gestion du stock">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button data-testid="stock-prefill-btn" variant="outline" className="h-9 px-3 text-xs" onClick={prefillStock} disabled={prefilling}><PackagePlus size={15} /> {prefilling ? "..." : "Pré-remplir (commandes)"}</Button>
            <Button data-testid="stock-thresholds-btn" variant="outline" className="h-9 px-3 text-xs" onClick={openThresholds}><SlidersHorizontal size={15} /> Seuils</Button>
            <Button data-testid="stock-restock-btn" variant="outline" className="h-9 px-3 text-xs" onClick={openRestock}><ClipboardList size={15} /> Commande réassort</Button>
            <Button data-testid="stock-moves-btn" variant="outline" className="h-9 px-3 text-xs" onClick={() => openMoves("")}><History size={15} /> Historique</Button>
          </div>
          <p className="text-xs text-muted-foreground">Stock par article, couleur et taille. Mettez à jour la quantité et le statut (En stock / À commander / Commandé). Le stock est déduit automatiquement quand un pack est marqué « reçu ». Une ligne est en alerte quand sa quantité est ≤ au seuil défini pour cet article et cette couleur.</p>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {articles.map((a) => (
              <div key={a.key} className="rounded-lg bg-secondary/40 p-3">
                <p className="font-bold text-sm mb-2">{a.label}</p>
                {colors.map((c) => { const sizes = (stock[a.key] || {})[c.key] || {}; const th = thOf(a.key, c.key); return (
                  <div key={c.key} className="mb-2">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{c.label} ({c.code}) · seuil {th}</p>
                    <div className="space-y-1.5">
                      {Object.keys(sizes).sort().map((sz) => { const cell = sizes[sz]; const low = (cell.qty || 0) <= th; return (
                        <div key={sz} data-testid={`stock-row-${a.key}-${c.key}-${sz}`} className={`flex items-center gap-2 rounded-md px-1 ${low ? "bg-destructive/10" : ""}`}>
                          <span className={`w-16 text-xs font-semibold flex items-center gap-1 ${low ? "text-destructive" : ""}`}>{low && <AlertTriangle size={11} />}{sz}</span>
                          <Input data-testid={`stock-qty-${a.key}-${c.key}-${sz}`} type="number" min="0" className={`w-20 ${low ? "border-destructive/50" : ""}`} value={cell.qty}
                            onChange={(e) => saveStockCell(a.key, c.key, sz, { qty: Math.max(0, parseInt(e.target.value) || 0) })} />
                          <Select data-testid={`stock-status-${a.key}-${c.key}-${sz}`} className="flex-1" value={cell.status}
                            onChange={(e) => saveStockCell(a.key, c.key, sz, { status: e.target.value })}>
                            {stockStatuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                          </Select>
                        </div>); })}
                      <div className="flex items-center gap-2">
                        <Select data-testid={`stock-add-size-${a.key}-${c.key}`} className="w-32" defaultValue=""
                          onChange={(e) => { if (e.target.value) { addStockSize(a.key, c.key, e.target.value); e.target.value = ""; } }}>
                          <option value="">+ Ajouter taille…</option>
                          {a.sizes.filter((s) => !(sizes[s])).map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                      </div>
                    </div>
                  </div>); })}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal open={threshOpen} onClose={() => setThreshOpen(false)} title="Seuils d'alerte de stock">
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Définissez le seuil d'alerte pour chaque article <b>et</b> chaque couleur. Une ligne de stock passe en alerte quand sa quantité est ≤ au seuil. Laissez vide pour utiliser le seuil par défaut ({lowThreshold}).</p>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {articles.map((a) => (
              <div key={a.key} className="rounded-lg bg-secondary/40 p-3">
                <p className="font-bold text-sm mb-2">{a.label}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {colors.map((c) => (
                    <div key={c.key}>
                      <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{c.label}</label>
                      <Input data-testid={`thresh-${a.key}-${c.key}`} type="number" min="0" placeholder={String(lowThreshold)}
                        value={(threshData[a.key] || {})[c.key] ?? ""} onChange={(e) => setThresh(a.key, c.key, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="save-thresholds-btn" className="w-full" onClick={saveThresholds} disabled={savingThresh}>{savingThresh ? "Enregistrement..." : "Enregistrer les seuils"}</Button>
        </div>
      </Modal>

      <Modal open={restockOpen} onClose={() => setRestockOpen(false)} title="Proposition de commande fournisseur">
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-muted-foreground">Références sous le seuil. Quantité proposée = seuil − stock actuel.</p>
            <div className="flex gap-2">
              <button data-testid="restock-pdf" onClick={() => downloadFile(`/equipment/restock.pdf?season=${season}`, "reassort_fournisseur.pdf").catch(() => toast.error("Export échoué"))} className="text-xs font-bold text-muted-foreground hover:text-foreground flex items-center gap-1"><FileText size={13} /> PDF</button>
              <button data-testid="restock-xlsx" onClick={() => downloadFile(`/equipment/restock.xlsx?season=${season}`, "reassort_fournisseur.xlsx").catch(() => toast.error("Export échoué"))} className="text-xs font-bold text-muted-foreground hover:text-foreground flex items-center gap-1"><Sheet size={13} /> Excel</button>
            </div>
          </div>
          {restock.lines > 0 && <p className="text-xs font-bold text-primary">{restock.lines} référence(s) · {restock.total_units} unité(s) à commander</p>}
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {restock.items.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Aucune référence sous le seuil. 👍</p> : (
              <table className="w-full text-xs" data-testid="restock-table">
                <thead><tr className="border-b border-border text-left text-muted-foreground uppercase">
                  <th className="py-2 pr-2">Article</th><th className="py-2 pr-2">Couleur</th><th className="py-2 pr-2">Taille</th>
                  <th className="py-2 pr-2 text-center">Stock</th><th className="py-2 pr-2 text-center">Seuil</th><th className="py-2 pr-2 text-center">À cmd.</th><th className="py-2">Réf.</th>
                </tr></thead>
                <tbody>
                  {restock.items.map((r, i) => (
                    <tr key={i} data-testid="restock-row" className="border-b border-border/50">
                      <td className="py-2 pr-2 font-semibold">{r.article}</td><td className="py-2 pr-2">{r.color}</td><td className="py-2 pr-2">{r.size}</td>
                      <td className="py-2 pr-2 text-center text-destructive font-bold">{r.qty}</td><td className="py-2 pr-2 text-center">{r.threshold}</td>
                      <td className="py-2 pr-2 text-center font-black text-primary">{r.to_order}</td><td className="py-2 text-muted-foreground">{r.supplier_ref || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      <Modal open={movesOpen} onClose={() => setMovesOpen(false)} title="Historique des mouvements de stock">
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Article</span>
            <Select data-testid="moves-article-filter" value={moveArt} onChange={(e) => filterMoves(e.target.value)} className="w-44">
              <option value="">Tous les articles</option>
              {articles.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </Select>
          </div>
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {moves.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Aucun mouvement enregistré.</p> : (
              <table className="w-full text-xs" data-testid="moves-table">
                <thead><tr className="border-b border-border text-left text-muted-foreground uppercase">
                  <th className="py-2 pr-2">Date</th><th className="py-2 pr-2">Article</th><th className="py-2 pr-2">Coul./Taille</th>
                  <th className="py-2 pr-2 text-center">Mvt</th><th className="py-2 pr-2 text-center">Reste</th><th className="py-2 pr-2">Motif</th><th className="py-2">Par</th>
                </tr></thead>
                <tbody>
                  {moves.map((m, i) => (
                    <tr key={i} data-testid="move-row" className="border-b border-border/50">
                      <td className="py-2 pr-2 whitespace-nowrap text-muted-foreground">{(m.at || "").replace("T", " ").slice(0, 16)}</td>
                      <td className="py-2 pr-2 font-semibold">{m.article_label}</td>
                      <td className="py-2 pr-2">{m.color_label} · {m.size}</td>
                      <td className={`py-2 pr-2 text-center font-black ${m.delta >= 0 ? "text-primary" : "text-destructive"}`}>{m.delta >= 0 ? `+${m.delta}` : m.delta}</td>
                      <td className="py-2 pr-2 text-center font-bold">{m.qty_after}</td>
                      <td className="py-2 pr-2 text-muted-foreground">{m.reason}</td>
                      <td className="py-2 text-muted-foreground">{m.user}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Ajouter un membre au pack">
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          <p className="text-xs text-muted-foreground">Sélectionnez un utilisateur (coach, comité, bénévole ou autre) à inclure dans le pack vêtements. Les coachs/comité y sont déjà par leur fonction.</p>
          {eligible.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Aucun utilisateur disponible.</p>}
          {eligible.map((u) => (
            <div key={u.id} data-testid={`eligible-user-${u.id}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-secondary/40">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{u.name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}{u.functions.length ? ` · ${u.functions.join(", ")}` : ""}</p>
              </div>
              {u.in_pack
                ? <span className="text-xs font-bold text-primary shrink-0">Déjà dans le pack</span>
                : <button data-testid={`add-user-${u.id}`} onClick={() => addPerson(u.id)} className="shrink-0 px-3 py-1.5 rounded-full text-xs font-bold bg-primary text-primary-foreground">Ajouter</button>}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
